import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarDays, ChevronLeft, ChevronRight, MapPin, Trophy } from 'lucide-react';
import proTournamentInfoService, { ProTournamentInfo } from '../../services/proTournamentInfoService';
import api from '../../services/api';

const ACCENT = '#3fced1';

const buildFileUrl = (path?: string | null): string | null => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  return `${api.defaults.baseURL}${path.startsWith('/') ? '' : '/'}${path}`;
};

/**
 * Sortierung: Das als Nächstes anstehende Turnier zuerst.
 * LIVE (läuft jetzt) → UPCOMING nach Startdatum aufsteigend →
 * ohne Datum → COMPLETED (neueste zuerst) ans Ende.
 */
export const sortByTimeline = (tournaments: ProTournamentInfo[]): ProTournamentInfo[] => {
  const rank = (t: ProTournamentInfo): number => {
    if (t.status === 'LIVE') return 0;
    if (t.status === 'UPCOMING') return 1;
    if (t.status === 'COMPLETED') return 3;
    return 2;
  };
  return [...tournaments].sort((a, b) => {
    const rankDiff = rank(a) - rank(b);
    if (rankDiff !== 0) return rankDiff;
    const aStart = typeof a.startDate === 'number' ? a.startDate : null;
    const bStart = typeof b.startDate === 'number' ? b.startDate : null;
    if (a.status === 'COMPLETED') {
      return (bStart ?? 0) - (aStart ?? 0); // Beendete: neueste zuerst
    }
    if (aStart == null && bStart == null) return 0;
    if (aStart == null) return 1;
    if (bStart == null) return -1;
    return aStart - bStart; // Kommende: nächstes zuerst
  });
};

const StatusBadge: React.FC<{ status?: string | null }> = ({ status }) => {
  if (status === 'LIVE') {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-red-500/90 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-white shadow-lg">
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" />
        Live
      </span>
    );
  }
  if (status === 'COMPLETED') {
    return (
      <span className="inline-flex items-center rounded-full bg-black/60 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-gray-300 backdrop-blur-sm">
        Beendet
      </span>
    );
  }
  return null;
};

const TournamentCard: React.FC<{
  tournament: ProTournamentInfo;
  onClick: () => void;
}> = ({ tournament, onClick }) => {
  const [imageError, setImageError] = useState(false);
  const thumbnail = buildFileUrl(tournament.thumbnail || tournament.heroImage);
  const showImage = thumbnail && !imageError;
  const orgTier = [tournament.organization, tournament.tier].filter(Boolean).join(' · ');

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={tournament.name || 'Pro-Turnier'}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      className="group w-[300px] shrink-0 cursor-pointer snap-start overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900 transition-all duration-300 hover:-translate-y-1 hover:border-[#3fced1]/50 hover:shadow-[0_16px_40px_-12px_rgba(63,206,209,0.3)] sm:w-[340px]"
    >
      {/* Banner (16:9 – KI-generiert) */}
      <div className="relative aspect-video w-full overflow-hidden bg-zinc-800">
        {showImage ? (
          <img
            src={thumbnail as string}
            alt={tournament.name || ''}
            loading="lazy"
            onError={() => setImageError(true)}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="relative flex h-full w-full items-center justify-center bg-gradient-to-br from-zinc-800 via-zinc-900 to-black">
            <div
              className="pointer-events-none absolute inset-0"
              style={{ background: 'radial-gradient(60% 80% at 30% 20%, rgba(63,206,209,0.12), transparent 65%)' }}
            />
            <Trophy size={36} className="text-zinc-600" />
          </div>
        )}
        <div className="absolute left-3 top-3">
          <StatusBadge status={tournament.status} />
        </div>
        {orgTier && (
          <span
            className="absolute right-3 top-3 rounded-full px-2.5 py-1 text-[11px] font-bold text-black shadow-lg"
            style={{ backgroundColor: ACCENT }}
          >
            {orgTier}
          </span>
        )}
      </div>

      {/* Info */}
      <div className="p-4">
        <h3 className="line-clamp-1 text-base font-bold text-white transition-colors group-hover:text-[#3fced1]">
          {tournament.shortName || tournament.name}
        </h3>
        <div className="mt-2 space-y-1.5 text-sm text-gray-400">
          <div className="flex items-center gap-1.5">
            <CalendarDays size={14} className="shrink-0" style={{ color: ACCENT }} />
            <span className="line-clamp-1">{tournament.dateLabel || 'Termin folgt'}</span>
          </div>
          {(tournament.venue || tournament.city) && (
            <div className="flex items-center gap-1.5">
              <MapPin size={14} className="shrink-0" style={{ color: ACCENT }} />
              <span className="line-clamp-1">
                {[tournament.venue, tournament.city].filter(Boolean).join(', ')}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Startseiten-Slider: Professionelle Turniere in Timeline-Reihenfolge –
 * das als Nächstes anstehende Turnier steht vorne. Klick → /turniere/:slug.
 */
const ProTournamentsSection: React.FC = () => {
  const navigate = useNavigate();
  const [tournaments, setTournaments] = useState<ProTournamentInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    proTournamentInfoService
      .list({ limit: 20 })
      .then((list) => {
        if (!cancelled) setTournaments(list.filter((t) => t && t.slug));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const sorted = useMemo(() => sortByTimeline(tournaments), [tournaments]);

  const scrollBy = (direction: 1 | -1) => {
    scrollRef.current?.scrollBy({ left: direction * 360, behavior: 'smooth' });
  };

  if (!loading && sorted.length === 0) return null;

  return (
    <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
      <div className="mb-5 flex items-center justify-between gap-3">
        <div>
          <h2 className="section-heading text-2xl font-bold tracking-tight text-white sm:text-3xl">
            Pro-Turniere
          </h2>
          <p className="mt-1 pl-[0.9rem] text-sm text-gray-400">
            Die großen Turniere der Darts-Welt – immer aktuell
          </p>
        </div>
        <div className="hidden gap-2 sm:flex">
          <button
            type="button"
            aria-label="Zurück scrollen"
            onClick={() => scrollBy(-1)}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-zinc-700 text-gray-300 transition-colors hover:border-[#3fced1]/60 hover:text-white"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            type="button"
            aria-label="Weiter scrollen"
            onClick={() => scrollBy(1)}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-zinc-700 text-gray-300 transition-colors hover:border-[#3fced1]/60 hover:text-white"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex gap-4 overflow-hidden">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="w-[300px] shrink-0 animate-pulse overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900 sm:w-[340px]"
            >
              <div className="aspect-video w-full bg-zinc-800" />
              <div className="space-y-2 p-4">
                <div className="h-5 w-3/4 rounded bg-zinc-800" />
                <div className="h-4 w-1/2 rounded bg-zinc-800" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div
          ref={scrollRef}
          className="-mx-4 flex snap-x snap-mandatory gap-4 overflow-x-auto px-4 pb-3 sm:-mx-6 sm:px-6 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {sorted.map((tournament) => (
            <TournamentCard
              key={tournament.slug || tournament.id}
              tournament={tournament}
              onClick={() => navigate(`/turniere/${tournament.slug}`)}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export default ProTournamentsSection;
